import { useEffect, useState, useMemo, useCallback } from 'react';
import { Box } from '@mui/material';
import { useUpdateNodeInternals } from 'reactflow';
import { color } from '@/colors';
import { useModelBaseView } from '@/components/Recipe/FlowContext';
import { HandleType } from '@/enums/handle-type.enum';
import { NodeViewMode } from '@/enums/node-view-mode.enum';
import { useBatchesByNodeId } from '../Recipe/RunFlow/batches.store';
import { ModelResults } from './ModelComponents/ModelResult';
import { ImportModelInput } from './ModelComponents/ImportModelInput';
import { use3DModelParameters } from './ModelComponents/use3DModelParameters';
import { ModelNodeFooter } from './ModelComponents/ModelNodeFooter/ModelNodeFooter';
import type { MenuAction } from '../Menu/Actions';
import type { NodeId } from 'web';
import type { AiModelData, UpdateNodeData } from '@/types/nodes/model';

interface ModelBaseComponentProps {
  data: AiModelData;
  isImportModelNode: boolean;
  hasValidModel: boolean;
  id: NodeId;
  isSelected: boolean;
  modelPrice?: number;
  selectedOutput: number;
  setSelectedOutput: (selectedOrUpdater: number | ((selected: number) => number)) => void;
  updateNodeData: UpdateNodeData;
  viewMode: NodeViewMode;
  selectedViewMode: NodeViewMode;
  additionalContextMenuItems?: MenuAction[];
}
function ModelBaseComponent({
  data,
  isImportModelNode,
  hasValidModel,
  id,
  isSelected,
  modelPrice,
  selectedOutput,
  setSelectedOutput,
  updateNodeData,
  viewMode,
  selectedViewMode,
  additionalContextMenuItems,
}: ModelBaseComponentProps) {
  const updateNodeInternals = useUpdateNodeInternals();
  const [errorMessage, setErrorMessage] = useState<string | object | null>(null);
  const modelBaseView = useModelBaseView(id);
  const { handles, model } = data;

  const batches = useBatchesByNodeId(id);
  const isProcessing = useMemo(() => {
    return batches.some((batch) =>
      batch.recipeRuns.some(
        (recipeRun) =>
          recipeRun.status === 'RUNNING' &&
          recipeRun.nodeRuns.some(
            (nodeRun) => nodeRun.nodeId === id && ['PENDING', 'RUNNING'].includes(nodeRun.status),
          ),
      ),
    );
  }, [batches, id]);

  // 3D viewer related
  const threeDProps = use3DModelParameters(id, data, updateNodeData);

  useEffect(() => {
    // this is to update the node internals when the input handles are exposed
    updateNodeInternals(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handles.input]);

  const nodeOutputType = useMemo(() => {
    if (typeof data.handles.output === 'object' && !Array.isArray(data.handles.output)) {
      const outputItems = Object.values(data.handles.output);
      const firstOutput = outputItems.find((item) => item.order === 0);
      return firstOutput?.type;
    }
    return HandleType.Image;
  }, [data.handles.output]);

  const handleTextChange = useCallback(
    (index: number, newValue: string) => {
      modelBaseView.editText(index, newValue);
    },
    [modelBaseView],
  );

  return (
    <Box sx={{ pointerEvents: 'auto' }}>
      {isImportModelNode && !hasValidModel ? (
        <ImportModelInput data={data} id={id} />
      ) : hasValidModel ? (
        <>
          <Box
            sx={{
              overflow: 'hidden',
              mb: 2,
              borderRadius: 2,
              border: '1px solid',
              borderColor: !isSelected ? color.White08_T : 'transparent',
            }}
          >
            <ModelResults
              data={data}
              id={id}
              shouldUseLegacyFileViewer={false}
              selected={selectedOutput}
              setSelected={setSelectedOutput}
              coverImage={model?.coverImage || undefined}
              noEmptyState={isImportModelNode}
              nodeOutputType={nodeOutputType}
              onTextChange={handleTextChange}
              threeDProps={threeDProps}
              isProcessing={isProcessing}
              viewMode={viewMode}
              selectedViewMode={selectedViewMode}
              additionalContextMenuItems={additionalContextMenuItems}
            />
          </Box>

          <ModelNodeFooter
            setErrorMessage={setErrorMessage}
            canSaveModel={isImportModelNode}
            data={data}
            errorMessage={typeof errorMessage === 'string' ? errorMessage : undefined}
            id={id}
            modelName={model?.name || ''}
            modelPrice={modelPrice}
            updateNodeData={updateNodeData}
          />
        </>
      ) : null}
    </Box>
  );
}

export default ModelBaseComponent;
